// Shared timer store + coordinator (Meeting Tools). Lives OUTSIDE the React tree
// (§9.3); only the timer pill and tool read it, and it changes only on state
// TRANSITIONS (start/pause/resume/reset/stop/done) — never per second — so it
// never re-renders a tile. The per-second display ticks inside the isolated
// TimerPill component (like the Elapsed / MeetingCountdown badges).
//
// Host-authoritative: the host broadcasts each transition with the remaining ms;
// every client (host included) anchors that remaining value to its own monotonic
// clock (performance.now) at the moment it's set, so wall-clock skew between
// machines is irrelevant. "Done" fires from a single setTimeout per run — the
// host also broadcasts it so late-joiners converge. Ephemeral; visual only —
// reaching 0 never ends the meeting.

import type { AttendeeSnapshot, TimerWire } from '@brizina/call-engine';
import { useStore } from 'zustand';
import { createStore } from 'zustand/vanilla';

import { engine } from '../engine';
import { playTimerDone } from '../sounds';
import { callStore } from './callStore';

export interface TimerView {
  status: 'off' | 'running' | 'paused' | 'done';
  durationMs: number;
  /** running: the remaining value captured at `anchor`; paused/done: frozen. */
  remainingMs: number;
  /** performance.now() reference the running countdown is measured from. */
  anchor: number;
}

const initial: TimerView = { status: 'off', durationMs: 0, remainingMs: 0, anchor: 0 };

export const timerStore = createStore<TimerView>(() => ({ ...initial }));
export function useTimerStore<T>(selector: (s: TimerView) => T): T {
  return useStore(timerStore, selector);
}

/** Live remaining ms right now (for the isolated pill's tick — NOT called in render). */
export function liveRemaining(s: TimerView): number {
  if (s.status === 'running') return Math.max(0, s.remainingMs - (performance.now() - s.anchor));
  return s.remainingMs;
}

let doneTimer: ReturnType<typeof setTimeout> | null = null;
const synced = new Set<string>();

function isHost(): boolean {
  return callStore.getState().isHost;
}
function clearDone(): void {
  if (doneTimer) {
    clearTimeout(doneTimer);
    doneTimer = null;
  }
}
/** One timeout per run reaches 0: flip to done, chime, and (host) tell everyone. */
function scheduleDone(remainingMs: number): void {
  clearDone();
  doneTimer = setTimeout(() => {
    doneTimer = null;
    const durationMs = timerStore.getState().durationMs;
    timerStore.setState({ status: 'done', remainingMs: 0, anchor: 0 });
    if (isHost()) engine.sendTimer({ t: 'state', status: 'done', remainingMs: 0, durationMs });
    playTimerDone();
  }, Math.max(0, remainingMs));
}

// ── Host controls ─────────────────────────────────────────────────────────
export function startTimer(durationMs: number): void {
  if (durationMs <= 0) return;
  timerStore.setState({ status: 'running', durationMs, remainingMs: durationMs, anchor: performance.now() });
  engine.sendTimer({ t: 'state', status: 'running', remainingMs: durationMs, durationMs });
  scheduleDone(durationMs);
}
export function pauseTimer(): void {
  const s = timerStore.getState();
  if (s.status !== 'running') return;
  const rem = liveRemaining(s);
  clearDone();
  timerStore.setState({ status: 'paused', remainingMs: rem, anchor: 0 });
  engine.sendTimer({ t: 'state', status: 'paused', remainingMs: rem, durationMs: s.durationMs });
}
export function resumeTimer(): void {
  const s = timerStore.getState();
  if (s.status !== 'paused' || s.remainingMs <= 0) return;
  timerStore.setState({ status: 'running', anchor: performance.now() });
  engine.sendTimer({ t: 'state', status: 'running', remainingMs: s.remainingMs, durationMs: s.durationMs });
  scheduleDone(s.remainingMs);
}
export function resetTimer(): void {
  const s = timerStore.getState();
  clearDone();
  timerStore.setState({ status: 'paused', remainingMs: s.durationMs, anchor: 0 });
  engine.sendTimer({ t: 'state', status: 'paused', remainingMs: s.durationMs, durationMs: s.durationMs });
}
export function stopTimer(): void {
  clearDone();
  timerStore.setState({ ...initial });
  engine.sendTimer({ t: 'clear' });
}

// ── Incoming (attendees apply; the host never receives its own) ─────────────
function handleTimer(_from: string, wire: TimerWire): void {
  if (wire.t === 'clear') {
    clearDone();
    timerStore.setState({ ...initial });
    return;
  }
  if (wire.status === 'running') {
    timerStore.setState({
      status: 'running',
      durationMs: wire.durationMs,
      remainingMs: wire.remainingMs,
      anchor: performance.now(),
    });
    scheduleDone(wire.remainingMs); // broadcast inside is host-gated → attendees just flash/chime locally
  } else if (wire.status === 'paused') {
    clearDone();
    timerStore.setState({ status: 'paused', durationMs: wire.durationMs, remainingMs: wire.remainingMs, anchor: 0 });
  } else {
    clearDone();
    timerStore.setState({ status: 'done', durationMs: wire.durationMs, remainingMs: 0, anchor: 0 });
  }
}

/** Late-join: the host re-sends the current timer (live remaining) to newcomers. */
function onAttendee(snap: AttendeeSnapshot): void {
  if (snap.isLocal || synced.has(snap.identity)) return;
  synced.add(snap.identity);
  if (!isHost()) return;
  const s = timerStore.getState();
  if (s.status === 'off') return;
  engine.sendTimer(
    { t: 'state', status: s.status, remainingMs: liveRemaining(s), durationMs: s.durationMs },
    snap.identity,
  );
}

export function resetTimerState(): void {
  clearDone();
  synced.clear();
  timerStore.setState({ ...initial });
}

engine.events.on('timer', handleTimer);
engine.events.on('attendeeUpserted', onAttendee);
